/**
 * The shape of one built-in tool: an operation the OS itself answers for a task, rather than a vendor's MCP server.
 * Every field a reader needs to call it correctly lives here, next to the exact text and schema the model is served,
 * so the model's view, the generated reference and the API all read the same definition.
 */

/** Which group a tool belongs to. A family decides the reference page it is documented on and who answers it. */
export type BuiltInToolFamily =
    | 'workspace'
    | 'memory'
    | 'os.execution'
    | 'os.execution_stop'
    | 'os.gate'
    | 'os.ceo'
    | 'os.recommendations'
    | 'os.mail'
    | 'os.mail_triage'
    | 'os.mail_chase'
    | 'os.my_mail'
    | 'os.escalation'
    | 'os.kpi'
    | 'os.context'
    | 'os.sense'
    | 'os.runs'
    | 'os.chat'
    | 'os.chat_threads'
    | 'os.planning'
    | 'os.org'
    | 'os.work'
    | 'computer-use'
    | 'web'
    | 'internal'

/**
 * Who can call a tool: a task's run, the CEO chat, the HTTP API, or the OS MCP server. `os` is the OS itself:
 * a name the OS acts under without anyone calling it — the gates it opens on its own account (a mail
 * escalation) carry one, so the gate names a registered tool rather than a string nothing documents.
 */
export type BuiltInToolCaller = 'run' | 'chat' | 'api' | 'mcp' | 'os'

/**
 * What the harness does with an error:
 * - `hand_back`: refused before the tool runs; the model reads why and gets another turn.
 * - `tool_error`: the tool ran and failed; the model reads the error as the tool's result and carries on.
 * - `fail`: the run ends with this reason.
 */
export type BuiltInToolErrorHandling = 'hand_back' | 'tool_error' | 'fail'

export type BuiltInToolError = {
    /** The reason code, as the invocation records it. A `:<detail>` suffix is part of the recorded reason. */
    code: string
    when: string
    handling: BuiltInToolErrorHandling
    /** What the model is told to do next, appended to the tool error it reads. */
    hint?: string
}

/** The subset of JSON Schema the built-in tools are served with. Key order is what the model reads. */
export type JsonSchema = {
    type?: 'object' | 'array' | 'string' | 'number' | 'integer' | 'boolean'
    description?: string
    properties?: Readonly<Record<string, JsonSchema>>
    required?: readonly string[]
    additionalProperties?: boolean
    items?: JsonSchema
    /** Each shape the value may take, as a field that takes one address or a list of them does (#1119). */
    anyOf?: readonly JsonSchema[]
    enum?: readonly (string | number)[]
    minimum?: number
    maximum?: number
    minItems?: number
}

/** What has to be true before a call can succeed. Each is a sentence a task author can act on. */
export type BuiltInToolNeeds = {
    /** `always`: the harness gates the call whatever the task declares. */
    gate?: 'always'
    /** Tools refused until this one has been called in the same run. */
    requiredBefore?: readonly string[]
    grant?: string
    writePolicy?: string
    input?: string
    member?: string
    binding?: string
}

/**
 * One answer a gate offers the person deciding it, in place of a bare Approve or Reject. A choice is a name over
 * one of those two decisions, so a gate with choices is decided, resumed and routed exactly as any other; what
 * it adds is the button's own words and what the person is asked for with it.
 */
/** A gate card's title (what approving does) and its why line (why it waits for a person), as `{field}` templates. */
export type GateCardTemplate = { title?: string; why?: string }

export type GateChoice = {
    /** Stored on the decided gate and handed to the next task as `ceoChoice`. */
    key: string
    /** The button. */
    label: string
    /** One line: what happens when it is chosen. */
    hint: string
    /** The decision it records. */
    decision: 'approve' | 'reject'
    /** What the person fills in with it, sent as the decision's note. `datetime` is sent as ISO 8601. */
    input?: { kind: 'text' | 'datetime'; label: string; required?: boolean }
}

export type BuiltInTool = {
    /** The name a task declares and the model calls (`.` becomes `__` on the wire). */
    name: string
    family: BuiltInToolFamily
    /** One line for tables and listings. */
    summary: string
    /** Exactly the description the model is served. */
    description: string
    /** Exactly the input schema the model is served. */
    inputSchema: JsonSchema
    returns?: string
    callers: readonly BuiltInToolCaller[]
    effect: 'read' | 'write'
    /** What a call changes beyond its declared effect: a read that marks something read, a refresh. */
    sideEffects?: string
    /**
     * - `safe`: repeating the call changes nothing.
     * - `idempotent`: it changes something, and a repeat of the same call acts once or leaves the same state.
     * - `unsafe`: a repeat acts again.
     */
    retry: 'safe' | 'idempotent' | 'unsafe'
    retryNote?: string
    needs?: BuiltInToolNeeds
    /** The choices this tool's gate offers instead of Approve and Reject. Only on a tool that is always gated. */
    gateChoices?: readonly GateChoice[]
    /**
     * The words this tool's gate card leads with, filled from the call it gates: `{field}` is the call's own field of
     * that name. A template naming a field the call left blank gives nothing, and the card says what it always did.
     */
    gateCard?: GateCardTemplate
    errors: readonly BuiltInToolError[]
    /** The HTTP endpoint that does the same thing, for a caller outside a run. */
    api?: { method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'; path: string; scope: string }
    examples?: readonly { input: Record<string, unknown>; note?: string }[]
    /** Whether the tool is documented and served by `GET /v1/tools/built-in`. Test fixtures are not. */
    public: boolean
    /** The sentence the chat system prompt uses for this tool. Only on tools chat offers. */
    chatPrompt?: string
    /**
     * The one job a chat tool is kept for (`create_task`): offered only in a conversation opened for that purpose,
     * never in a member's everyday thread. Unset on every other tool.
     */
    purpose?: string
}

/** The open schema a tool with no declared fields is served: any object at all. */
export const OPEN_OBJECT_SCHEMA: JsonSchema = { type: 'object', additionalProperties: true }
